// src/components/CoverLetterModal.tsx
import {
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonContent,
  IonHeader,
  IonIcon,
  IonModal,
  IonSpinner,
  IonText,
  IonTextarea,
  IonTitle,
  IonToolbar,
  useIonToast,
} from '@ionic/react';
import { closeCircleOutline, closeOutline, copyOutline, createOutline, documentTextOutline, warningOutline } from 'ionicons/icons';
import React, { useEffect, useState } from 'react';
import { coverLetter } from '../services/api.ts';

interface CoverLetterModalProps {
  isOpen: boolean;
  onClose: () => void;
  jobId: string | null;
  jobTitle?: string;
  companyName?: string;
  resumeId?: string;
  userSearchId?: string;
  isScrapedJob?: boolean;
}

type CoverLetterLength = 'concise' | 'medium' | 'detailed';

const CoverLetterModal: React.FC<CoverLetterModalProps> = ({
  isOpen,
  onClose,
  jobId,
  jobTitle,
  companyName,
  resumeId,
  userSearchId,
  isScrapedJob
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [coverLetterText, setCoverLetterText] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);
  const [presentToast] = useIonToast();
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isFromCache, setIsFromCache] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [selectedLength, setSelectedLength] = useState<CoverLetterLength | null>(null);
  const [showLengthSelection, setShowLengthSelection] = useState(true);
  const [insufficientBalance, setInsufficientBalance] = useState(false);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCoverLetterText('');
      setIsEditing(false);
      setError(null);
      setHasLoaded(false);
      setIsFromCache(false);
      setLoadingMessage('');
      setSelectedLength(null);
      setShowLengthSelection(true);
      setInsufficientBalance(false);
    }
  }, [isOpen]);

  // Listen for insufficient balance modal close events
  useEffect(() => {
    const handleInsufficientBalanceModalClose = () => {
      // When insufficient balance modal closes, check if we should close this modal too
      if (insufficientBalance) {
        // Small delay to ensure smooth transition
        setTimeout(() => {
          onClose();
        }, 300);
      }
    };

    window.addEventListener('insufficient-balance-modal-closed', handleInsufficientBalanceModalClose);
    return () => {
      window.removeEventListener('insufficient-balance-modal-closed', handleInsufficientBalanceModalClose);
    };
  }, [insufficientBalance, onClose]);

  const handleLengthSelection = async (length: CoverLetterLength) => {
    setSelectedLength(length);
    setShowLengthSelection(false);
    await fetchCoverLetter(length);
  };

  const fetchCoverLetter = async (length: CoverLetterLength) => {
    if (!jobId) return;

    setLoading(true);
    setError(null);
    setCoverLetterText('');
    setIsEditing(false);
    setIsFromCache(false);

    // Start with checking for existing letter
    setLoadingMessage('Checking for existing cover letter...');

    // Set up a timer to change the message if it's taking longer (likely generating new)
    const messageTimer = setTimeout(() => {
      setLoadingMessage('Crafting your personalized cover letter...');
    }, 2000); // After 2 seconds, assume we're generating

    try {
      const startTime = Date.now();
      const result = await coverLetter.generateCoverLetter(jobId, length, resumeId, userSearchId, isScrapedJob);
      const endTime = Date.now();
      const duration = endTime - startTime;

      // Clear the message timer since we got a response
      clearTimeout(messageTimer);

      // If response came back very quickly (< 2 seconds), it's likely from cache
      const isQuickResponse = duration < 2000;

      if (isQuickResponse) {
        setLoadingMessage('Retrieved your saved cover letter');
        setIsFromCache(true);
        // No delay for cached responses - show immediately
      } else {
        setLoadingMessage('Cover letter generated successfully!');
        setIsFromCache(false);
        // Small delay for new generations to show success message
        await new Promise(resolve => setTimeout(resolve, 600));
      }

      setCoverLetterText(result.cover_letter || result.text || '');
      setHasLoaded(true);

      // End loading state
      setLoading(false);
      setLoadingMessage('');

      return;
    } catch (err: any) {
      // Clear the message timer on error
      clearTimeout(messageTimer);

      console.error('Failed to generate cover letter:', err);
      
      // Handle 402 Payment Required (Insufficient Balance)
      // The API interceptor will show the InsufficientBalanceModal,
      // but we also need to handle the state here so modal doesn't become blank
      if (err.response?.status === 402) {
        setLoading(false);
        setLoadingMessage('');
        setInsufficientBalance(true);
        // Don't set error - let InsufficientBalanceModal handle the main message
        return;
      }
      
      // For other errors, extract message properly
      const detail = err.response?.data?.detail;
      let errorMessage: string;
      
      if (typeof detail === 'object' && detail !== null && !Array.isArray(detail)) {
        // If detail is an object, extract the message field
        errorMessage = detail.message || detail.error || JSON.stringify(detail);
      } else if (typeof detail === 'string') {
        // If detail is already a string, use it
        errorMessage = detail;
      } else {
        // Fallback to error message or default
        errorMessage = err.message || 'Failed to generate cover letter';
      }
      
      setError(errorMessage);
      setLoading(false);
      setLoadingMessage('');
      setShowLengthSelection(true); // Show length selection again on error
    }
  };

  // Handle when user tries to close modal
  const handleCanDismiss = async () => {
    if (isEditing) {
      // If editing, just exit edit mode and prevent modal from closing
      setIsEditing(false);
      return false;
    }
    // Allow modal to close
    return true;
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(coverLetterText);
      presentToast({
        message: 'Cover letter copied to clipboard!',
        duration: 2000,
        color: 'success',
        position: 'bottom',
      });
    } catch (err) {
      presentToast({
        message: 'Failed to copy to clipboard',
        duration: 2000,
        color: 'danger',
        position: 'bottom',
      });
    }
  };

  const handleEdit = () => {
    setIsEditing(!isEditing);
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onClose}
      canDismiss={handleCanDismiss}
    >
      <IonHeader>
        <IonToolbar>
          <IonTitle>Generate Cover Letter</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => {
              if (isEditing) {
                setIsEditing(false);
              } else {
                onClose();
              }
            }}>
              <IonIcon icon={closeOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">
        {/* Job Info */}
        {(jobTitle || companyName) && (
          <div style={{ marginBottom: '20px' }}>
            <IonText>
              <h2 style={{
                marginTop: 0,
                fontSize: '1.3rem',
                fontWeight: 600,
                color: '#1e3a8a',
                marginBottom: '4px'
              }}>
                {jobTitle}
              </h2>
              {companyName && (
                <p style={{
                  margin: 0,
                  fontSize: '1rem',
                  color: '#6b7280',
                  fontWeight: 500
                }}>
                  {companyName}
                </p>
              )}
            </IonText>
          </div>
        )}

        {/* Length Selection */}
        {showLengthSelection && !loading && !coverLetterText && !insufficientBalance && (
          <div style={{ marginBottom: '24px' }}>
            <IonText>
              <h3 style={{
                marginTop: 0,
                marginBottom: '16px',
                fontSize: '1.1rem',
                fontWeight: 600,
                color: '#1f2937'
              }}>
                Choose your cover letter length:
              </h3>
            </IonText>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Concise Option */}
              <IonCard
                button
                onClick={() => handleLengthSelection('concise')}
                style={{
                  margin: 0,
                  cursor: 'pointer',
                  '--background': '#ffffff',
                  border: '2px solid #e5e7eb',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#10b981';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <IonCardContent style={{ padding: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <div style={{
                      minWidth: '36px',
                      height: '36px',
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '18px'
                    }}>
                      ⚡
                    </div>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ margin: '0 0 6px', fontSize: '1rem', fontWeight: 600, color: '#1f2937' }}>
                        Concise & Focused
                      </h4>
                      <p style={{ margin: 0, fontSize: '0.9rem', color: '#6b7280', lineHeight: '1.5' }}>
                        Brief and impactful. Highlights key qualifications in a compact format (2-3 paragraphs).
                      </p>
                    </div>
                  </div>
                </IonCardContent>
              </IonCard>

              {/* Medium Option */}
              <IonCard
                button
                onClick={() => handleLengthSelection('medium')}
                style={{
                  margin: 0,
                  cursor: 'pointer',
                  '--background': '#ffffff',
                  border: '2px solid #e5e7eb',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <IonCardContent style={{ padding: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <div style={{
                      minWidth: '36px',
                      height: '36px',
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '18px'
                    }}>
                      ✨
                    </div>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ margin: '0 0 6px', fontSize: '1rem', fontWeight: 600, color: '#1f2937' }}>
                        Standard Length
                      </h4>
                      <p style={{ margin: 0, fontSize: '0.9rem', color: '#6b7280', lineHeight: '1.5' }}>
                        Balanced approach. Covers experience and skills with relevant details (3-4 paragraphs).
                      </p>
                    </div>
                  </div>
                </IonCardContent>
              </IonCard>

              {/* Detailed Option */}
              <IonCard
                button
                onClick={() => handleLengthSelection('detailed')}
                style={{
                  margin: 0,
                  cursor: 'pointer',
                  '--background': '#ffffff',
                  border: '2px solid #e5e7eb',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#8b5cf6';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(139, 92, 246, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <IonCardContent style={{ padding: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <div style={{
                      minWidth: '36px',
                      height: '36px',
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '18px'
                    }}>
                      📝
                    </div>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ margin: '0 0 6px', fontSize: '1rem', fontWeight: 600, color: '#1f2937' }}>
                        Comprehensive
                      </h4>
                      <p style={{ margin: 0, fontSize: '0.9rem', color: '#6b7280', lineHeight: '1.5' }}>
                        Thorough coverage. Detailed examples and extensive background (4-5 paragraphs).
                      </p>
                    </div>
                  </div>
                </IonCardContent>
              </IonCard>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '60px 20px',
            minHeight: '300px'
          }}>
            {/* Animated Loader */}
            <div style={{
              position: 'relative',
              width: '80px',
              height: '80px',
              marginBottom: '32px'
            }}>
              {/* Outer rotating circle */}
              <div style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                border: '3px solid #e5e7eb',
                borderTop: '3px solid #10b981',
                borderRadius: '50%',
                animation: 'spin 1.2s linear infinite'
              }} />

              {/* Inner pulsing circle */}
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '50px',
                height: '50px',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                borderRadius: '50%',
                animation: 'pulse 1.5s ease-in-out infinite'
              }} />

              {/* Document icon in center */}
              <IonIcon
                icon={documentTextOutline}
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  fontSize: '28px',
                  color: 'white',
                  zIndex: 1
                }}
              />
            </div>

            {/* Loading Message */}
            <h3 style={{
              margin: '0 0 12px',
              fontSize: '1.25rem',
              fontWeight: 600,
              color: '#1f2937',
              textAlign: 'center'
            }}>
              {loadingMessage || 'Processing...'}
            </h3>

            {/* Subtext */}
            <p style={{
              margin: 0,
              fontSize: '0.95rem',
              color: '#6b7280',
              textAlign: 'center',
              maxWidth: '300px',
              lineHeight: '1.5'
            }}>
              {isFromCache
                ? 'We found your previously generated cover letter'
                : 'AI is crafting a professional cover letter tailored to this position'
              }
            </p>

            {/* Progress dots */}
            <div style={{
              display: 'flex',
              gap: '8px',
              marginTop: '24px'
            }}>
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: '#10b981',
                    animation: `dotPulse 1.4s ease-in-out ${i * 0.2}s infinite`
                  }}
                />
              ))}
            </div>

            {/* Add animations */}
            <style>{`
              @keyframes spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
              }

              @keyframes pulse {
                0%, 100% {
                  transform: translate(-50%, -50%) scale(1);
                  opacity: 1;
                }
                50% {
                  transform: translate(-50%, -50%) scale(0.95);
                  opacity: 0.8;
                }
              }

              @keyframes dotPulse {
                0%, 100% {
                  opacity: 0.2;
                  transform: scale(0.8);
                }
                50% {
                  opacity: 1;
                  transform: scale(1.2);
                }
              }
            `}</style>
          </div>
        )}

        {/* Insufficient Balance State */}
        {insufficientBalance && !loading && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '60px 20px',
            minHeight: '300px',
            textAlign: 'center'
          }}>
            <IonIcon
              icon={warningOutline}
              style={{
                fontSize: '64px',
                color: '#f59e0b',
                marginBottom: '24px'
              }}
            />
            <h3 style={{
              margin: '0 0 12px',
              fontSize: '1.25rem',
              fontWeight: 600,
              color: '#1f2937'
            }}>
              Insufficient Credits
            </h3>
            <p style={{
              margin: '0 0 24px',
              fontSize: '0.95rem',
              color: '#6b7280',
              maxWidth: '300px',
              lineHeight: '1.5'
            }}>
              You don't have enough credits to generate a cover letter. Please add credits to continue.
            </p>
            <IonButton
              expand="block"
              fill="solid"
              onClick={onClose}
              style={{
                maxWidth: '200px',
                '--background': '#10b981',
                '--color': 'white',
                fontWeight: 600,
                textTransform: 'none'
              }}
            >
              Close
            </IonButton>
          </div>
        )}

        {/* Error State */}
        {error && !loading && !insufficientBalance && (
          <IonCard style={{ '--background': '#fff3f3', border: '1px solid #eb445a' }}>
            <IonCardContent>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <IonIcon icon={closeCircleOutline} style={{ fontSize: '24px', color: '#eb445a' }} />
                <div>
                  <IonText color="danger">
                    <strong>Generation Failed</strong>
                  </IonText>
                  <p style={{ margin: '4px 0 0', fontSize: '0.9rem', color: '#666' }}>{error}</p>
                </div>
              </div>
              <IonButton expand="block" fill="outline" color="danger" onClick={() => fetchCoverLetter(selectedLength || 'medium')} style={{ marginTop: '16px' }}>
                Try Again
              </IonButton>
            </IonCardContent>
          </IonCard>
        )}

        {/* Success State - Show Cover Letter */}
        {coverLetterText && !loading && !insufficientBalance && (
          <>
            {/* Cache Notification Banner */}
            {isFromCache && (
              <div style={{
                background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                border: '1px solid #a7f3d0',
                borderRadius: '12px',
                padding: '16px 20px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                boxShadow: '0 2px 8px rgba(16, 185, 129, 0.1)',
                animation: 'slideDown 0.4s ease-out'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <span style={{ fontSize: '20px' }}>✨</span>
                </div>
                <div style={{ flex: 1 }}>
                  <h4 style={{
                    margin: '0 0 4px',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    color: '#059669'
                  }}>
                    Previously Generated Cover Letter
                  </h4>
                  <p style={{
                    margin: 0,
                    fontSize: '0.85rem',
                    color: '#047857',
                    lineHeight: '1.4'
                  }}>
                    We found your saved cover letter for this job. Feel free to edit it before copying.
                  </p>
                </div>
              </div>
            )}

            {/* Add slide down animation */}
            <style>{`
              @keyframes slideDown {
                from {
                  opacity: 0;
                  transform: translateY(-10px);
                }
                to {
                  opacity: 1;
                  transform: translateY(0);
                }
              }
            `}</style>

            {/* Cover Letter Card */}
            <IonCard style={{ '--background': '#ffffff', border: '1px solid #e9ecef', marginBottom: '20px' }}>
              <IonCardContent style={{ padding: '20px' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '16px',
                  flexWrap: 'wrap',
                  gap: '12px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <IonIcon icon={documentTextOutline} style={{ fontSize: '24px', color: '#1e3a8a' }} />
                    <IonText>
                      <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: '#212529' }}>
                        Your Cover Letter
                      </h3>
                    </IonText>
                  </div>

                  {/* Cache Badge */}
                  {isFromCache && (
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 12px',
                      background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                      borderRadius: '20px',
                      border: '1px solid #a7f3d0'
                    }}>
                      <div style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: '#10b981'
                      }} />
                      <span style={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: '#059669',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Previously Generated
                      </span>
                    </div>
                  )}
                </div>

                {/* Text Area with Copy Button */}
                <div style={{ position: 'relative' }}>
                  {isEditing ? (
                    <IonTextarea
                      value={coverLetterText}
                      onIonInput={(e) => setCoverLetterText(e.detail.value!)}
                      rows={20}
                      style={{
                        '--background': '#f8f9fa',
                        '--color': '#212529',
                        '--padding-start': '12px',
                        '--padding-end': '12px',
                        '--padding-top': '12px',
                        '--padding-bottom': '12px',
                        border: '2px solid #667eea',
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        lineHeight: '1.6',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      }}
                    />
                  ) : (
                    <>
                      {/* Copy Button - Top Right */}
                      <IonButton
                        onClick={handleCopy}
                        fill="clear"
                        style={{
                          position: 'absolute',
                          top: '0px',
                          right: '0px',
                          zIndex: 10,
                          '--color': '#6b7280',
                          '--padding-start': '8px',
                          '--padding-end': '8px',
                          '--padding-top': '8px',
                          '--padding-bottom': '8px',
                          minHeight: 'auto',
                          height: '36px',
                        }}
                        title="Copy to clipboard"
                      >
                        <IonIcon icon={copyOutline} style={{ fontSize: '20px' }} />
                      </IonButton>

                      <div
                        onClick={() => setIsEditing(true)}
                        style={{
                          whiteSpace: 'pre-wrap',
                          fontSize: '0.95rem',
                          lineHeight: '1.6',
                          color: '#212529',
                          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                          cursor: 'pointer',
                          padding: '8px',
                          paddingTop: '40px', // Make room for copy button
                          borderRadius: '8px',
                          transition: 'background-color 0.2s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f8f9fa';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                        title="Click to edit"
                      >
                        {coverLetterText}
                      </div>
                    </>
                  )}
                </div>
              </IonCardContent>
            </IonCard>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
              <IonButton
                expand="block"
                fill={isEditing ? 'solid' : 'outline'}
                onClick={handleEdit}
                style={{
                  '--background': isEditing ? '#10b981' : 'transparent',
                  '--border-color': isEditing ? '#10b981' : '#6b7280',
                  '--color': isEditing ? 'white' : '#6b7280',
                  fontWeight: 600,
                  textTransform: 'none',
                }}
              >
                <IonIcon icon={createOutline} slot="start" />
                {isEditing ? 'Done Editing' : 'Edit Cover Letter'}
              </IonButton>

              <IonButton
                expand="block"
                fill="clear"
                onClick={() => {
                  setIsEditing(false);
                  onClose();
                }}
                style={{
                  '--color': '#6b7280',
                  fontWeight: 500,
                  textTransform: 'none',
                }}
              >
                Close
              </IonButton>
            </div>
          </>
        )}
      </IonContent>
    </IonModal>
  );
};

export default CoverLetterModal;
